/* istanbul ignore file */
import React from 'react';
import { ContentAttribute, ContentEntry, Maybe } from '../graphql/graphql';
import attributesArrayToProps from '../helpers/attributesArrayToProps';

//
// Adding a new Car (component) to the CHOO CHOO TRAIN?
// Step 1) import prop types for the Choo Choo train car. Then look for Step 2 below.
//
import { type AccordionItemProps } from '../AccordionItem/AccordionItem';
import { type AccordionProps } from '../Accordion/Accordion';
import { type BlockImageProps } from '../BlockImage/BlockImage';
import { type CardBlockProps } from '../CardBlock/CardBlock';
import { type CategoryCardProps } from '../CategoryCard/CategoryCard';
import { type CategoryCardsProps } from '../CategoryCards/CategoryCardsTypes';
import { type CategoryCardsTitleAndSubtitleProps } from '../CategoryCardTitleAndSubtitle/CategoryCardTitleAndSubtitle';
import { type ChooTitleProps } from '../ChooTitle/ChooTitle';
import { type ContentBlockProps } from '../ContentBlock/ContentBlock';
import { type CtasGroupProps } from '../CtasGroup/CtasGroup';
import { type DefaultProps } from '../Default/Default';
import { type DynamicBlockProps } from '../DynamicBlock/DynamicBlock';
import { type DynamicContentProps } from '../DynamicContent/DynamicContent';
import { type EditorialProps } from '../Editorial/Editorial';
import { type EmptyBoxProps } from '../EmptyBox/EmptyBox';
import { type GlobalBannerProps } from '../GlobalBanner/GlobalBanner';
import { type HtmlProps } from '../Html/Html';
import { type ImageProps } from '../Image/Image';
import { type LayoutProps } from '../Layout/Layout';
import { type LinkProps } from '../Link/Link';
import { type OverlayedContentItemsProps } from '../OverlayedContentItems/OverlayedContentItems';
import { type PageContentDesktopProps } from '../PageContentDesktop/PageContentDesktop';
import { type PageContentProps } from '../PageContent/PageContent';
import { type PageProps } from '../Page/Page';
import { type ReferenceCardBlockProps } from '../ReferenceCardBlock/ReferenceCardBlock';
import { type ReferenceContentBlockProps } from '../ReferenceContentBlock/ReferenceContentBlock';
import { type ReferenceDynamicBlockProps } from '../ReferenceDynamicBlock/ReferenceDynamicBlock';
import { type RichTextComponentProps } from '../RichTextComponent/RichTextComponent';
import { type SpacerProps } from '../Spacer/Spacer';
import { type VideoProps } from '../Video/Video';

//
// Step 2) Lazy import the CHOO CHOO TRAIN component
//
const Accordion = React.lazy(() => import('../Accordion/Accordion'));
const AccordionItem = React.lazy(
  () => import('../AccordionItem/AccordionItem')
);
const BlockImage = React.lazy(() => import('../BlockImage/BlockImage'));
const CardBlock = React.lazy(() => import('../CardBlock/CardBlock'));
const CategoryCard = React.lazy(() => import('../CategoryCard/CategoryCard'));
const CategoryCards = React.lazy(
  () => import('../CategoryCards/CategoryCards')
);
const CategoryCardsTitleAndSubtitle = React.lazy(
  () => import('../CategoryCardTitleAndSubtitle/CategoryCardTitleAndSubtitle')
);
const ChooTitle = React.lazy(() => import('../ChooTitle/ChooTitle'));
const ContentBlock = React.lazy(() => import('../ContentBlock/ContentBlock'));
const CtasGroup = React.lazy(() => import('../CtasGroup/CtasGroup'));
const Default = React.lazy(() => import('../Default/Default'));
const DynamicBlock = React.lazy(() => import('../DynamicBlock/DynamicBlock'));
const DynamicContent = React.lazy(
  () => import('../DynamicContent/DynamicContent')
);
const Editorial = React.lazy(() => import('../Editorial/Editorial'));
const EmptyBox = React.lazy(() => import('../EmptyBox/EmptyBox'));
const GlobalBanner = React.lazy(() => import('../GlobalBanner/GlobalBanner'));
const Html = React.lazy(() => import('../Html/Html'));
const Image = React.lazy(() => import('../Image/Image'));
const Layout = React.lazy(() => import('../Layout/Layout'));
const Link = React.lazy(() => import('../Link/Link'));
const OverlayedContentItems = React.lazy(
  () => import('../OverlayedContentItems/OverlayedContentItems')
);
const Page = React.lazy(() => import('../Page/Page'));
const PageContent = React.lazy(() => import('../PageContent/PageContent'));
const PageContentDesktop = React.lazy(
  () => import('../PageContentDesktop/PageContentDesktop')
);
const ReferenceCardBlock = React.lazy(
  () => import('../ReferenceCardBlock/ReferenceCardBlock')
);
const ReferenceContentBlock = React.lazy(
  () => import('../ReferenceContentBlock/ReferenceContentBlock')
);
const ReferenceDynamicBlock = React.lazy(
  () => import('../ReferenceDynamicBlock/ReferenceDynamicBlock')
);
const RichTextComponent = React.lazy(
  () => import('../RichTextComponent/RichTextComponent')
);
const Spacer = React.lazy(() => import('../Spacer/Spacer'));
const Video = React.lazy(() => import('../Video/Video'));

export function hasEntries(
  entries: Maybe<Maybe<ContentEntry>[]> | undefined
): boolean {
  return Array.isArray(entries) && entries.length > 0;
}

export function adapter<T>(
  Component: React.ComponentType<T>,
  attributes: Maybe<ContentAttribute>[],
  entries: Maybe<Maybe<ContentEntry>[]> | undefined,
  key: string,
  index?: number
) {
  const props = attributesArrayToProps<T>(attributes);
  if (hasEntries(entries)) {
    return (
      <Component key={key} {...props} index={index}>
        <ChooInception node={entries} />
      </Component>
    );
  }

  return <Component key={key} {...props} index={index} />;
}

export type ChooInceptionProps = {
  node?: Maybe<Maybe<ContentEntry>[]>;
};

export const ChooInception = React.memo(function ChooInception({
  node,
}: ChooInceptionProps) {
  return (
    <>
      {node &&
        node
          .filter((entry) => !!entry)
          .map((entry, index) => {
            const { type, attributes, entries } = entry;
            // use the UID from content stack as the unique key, if not, fall
            // back to a very long but unique string.
            const key = String(
              attributes.find((arr) => arr?.key === 'uid')?.value ??
                attributes.find((arr) => arr?.key === 'id')?.value ??
                JSON.stringify(entry)
            );
            // Adding original type as n attribute
            // Copying the array because attributes array is not extensible.
            const attributesWithType = JSON.parse(JSON.stringify(attributes));
            attributesWithType.push({
              key: 'data-type',
              value: type,
            });

            // THIS IS THE MAIN TRAIN! (try to keep the code alphabetically sorted)
            // --------------------------------------------------------------------
            if (type === 'accordion') {
              return adapter<AccordionProps>(
                Accordion,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'accordion_item') {
              return adapter<AccordionItemProps>(
                AccordionItem,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'block_image' || type === 'card_image') {
              return adapter<BlockImageProps>(
                BlockImage,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'card_block') {
              return adapter<CardBlockProps>(
                CardBlock,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'card_title_and_subtitle') {
              return adapter<CategoryCardsTitleAndSubtitleProps>(
                CategoryCardsTitleAndSubtitle,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'ctas' || type === 'block_ctas') {
              return adapter<LinkProps>(Link, attributesWithType, entries, key);
            } else if (
              type === 'block_title' ||
              type === 'block_subtitle' ||
              type === 'card_block_title' ||
              type === 'card_block_subtitle'
            ) {
              return adapter<ChooTitleProps>(
                ChooTitle,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'category_card') {
              return adapter<CategoryCardProps>(
                CategoryCard,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'category_cards') {
              return adapter<CategoryCardsProps>(
                CategoryCards,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'content_block') {
              return adapter<ContentBlockProps>(
                ContentBlock,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'ctas_group') {
              return adapter<CtasGroupProps>(
                CtasGroup,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'dynamic_block') {
              return adapter<DynamicBlockProps>(
                DynamicBlock,
                attributesWithType,
                entries,
                key
              );
            } else if (
              type === 'dynamic_accordion' ||
              type === 'dynamic_cta' ||
              type === 'dynamic_editorial' ||
              type === 'dynamic_image' ||
              type === 'dynamic_height' ||
              type === 'dynamic_empty_box' ||
              type === 'dynamic_video'
            ) {
              return adapter<DynamicContentProps>(
                DynamicContent,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'empty_box') {
              return adapter<EmptyBoxProps>(
                EmptyBox,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'editorial') {
              return adapter<EditorialProps>(
                Editorial,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'global_banner') {
              return adapter<GlobalBannerProps>(
                GlobalBanner,
                attributesWithType,
                entries,
                key,
                index
              );
            } else if (type === 'html') {
              return adapter<HtmlProps>(Html, attributesWithType, entries, key);
            } else if (type === 'image') {
              return adapter<ImageProps>(
                Image,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'link') {
              return adapter<LinkProps>(Link, attributesWithType, entries, key);
            } else if (type === 'overlayed_content_items') {
              return adapter<OverlayedContentItemsProps>(
                OverlayedContentItems,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'page_content_desktop') {
              return adapter<PageContentDesktopProps>(
                PageContentDesktop,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'page_content') {
              return adapter<PageContentProps>(
                PageContent,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'page_section') {
              return adapter<LayoutProps>(
                Layout,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'page') {
              return adapter<PageProps>(Page, attributesWithType, entries, key);
            } else if (type === 'reference_content_block') {
              return adapter<ReferenceContentBlockProps>(
                ReferenceContentBlock,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'rich_text') {
              return adapter<RichTextComponentProps>(
                RichTextComponent,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'reference_card_block') {
              return adapter<ReferenceCardBlockProps>(
                ReferenceCardBlock,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'reference_dynamic_block') {
              return adapter<ReferenceDynamicBlockProps>(
                ReferenceDynamicBlock,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'spacer') {
              return adapter<SpacerProps>(
                Spacer,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'video' || type === 'block_video') {
              return adapter<VideoProps>(
                Video,
                attributesWithType,
                entries,
                key
              );
            } else if (type === 'dynamic_rich_text') {
              return adapter<RichTextComponentProps>(
                RichTextComponent,
                attributesWithType,
                entries,
                key
              );
            }

            //
            // ⬆️ ⬆️ Adding a new Car (component) to the CHOO CHOO TRAIN?
            // Step 2) ADD MORE CHOO CHOO TRAIN CARS ABOVE HERE!
            // add a new else if block and copy the format of the other blocks above.
            //

            //
            // KEEP THIS "ELSE" BLOCK AS THE LAST COMPONENT
            //
            else {
              // find the original type so we can pass it as a data attribute
              // this is useful for debugging
              return adapter<DefaultProps>(
                Default,
                attributesWithType,
                entries,
                key
              );
            }
          })}
    </>
  );
});

ChooInception.displayName = 'ChooInception';

export default ChooInception;
